import React, { useState, useEffect } from "react";
import { useUser } from "../UserContext"; // Access teacher data from context
import { useNavigate } from "react-router-dom";
import config from "../config"; // Import the config file

const TeacherDash = () => {
  const { user } = useUser(); // Access teacher's data
  const [students, setStudents] = useState([]); // Students list
  const [selectedStudent, setSelectedStudent] = useState(null); // Student to update
  const [subjectToUpdate, setSubjectToUpdate] = useState(""); // Subject being edited
  const [updatedResult, setUpdatedResult] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null); // Error state
  const [success, setSuccess] = useState(null)
  const navigate = useNavigate();

  

  // Fetch students from the database
  const fetchStudents = async () => {
    setLoading(true);
    setError(null);
  
    try {
      if (!user || !user.Classes || user.Classes.length === 0) {
        throw new Error("No classes available for the current user.");
      }
  
      const allStudents = [];
  
      for (const className of user.Classes) {
        const response = await fetch(`${config.apiEndpoint}/${className}`);
        if (!response.ok) {
          throw new Error(`Failed to fetch data for class: ${className}`);
        }
  
        const classData = await response.json();
  
        // Debugging: Log class data
        console.log(`Data for ${className}:`, classData);
  
        if (Array.isArray(classData)) {
          const filteredStudents = classData
            .filter((student) => user.Section.includes(student.Section))
            .map((student) => ({
              ...student,
              Results: Array.isArray(student.Results) ? student.Results : [], // Ensure Results is an array
            }));
          allStudents.push(...filteredStudents); // Add to master list
        }
      }
  
      if (allStudents.length === 0) {
        throw new Error("No students found for your section and classes.");
      }
  
      setStudents(allStudents);
    } catch (err) {
      console.error(err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
    const handleRemarkChange = (studentId, newRemark) => {
      const updatedStudents = students.map((s) =>
        s.id === studentId ? { ...s, Remark: newRemark } : s
      );
      setStudents(updatedStudents);
    };
  
    const saveRemark = async (studentId) => {
      const student = students.find((s) => s.id === studentId);
      if (!student) return;
  
      try {
        const response = await fetch(`${config.apiEndpoint}/${student.Class}/${studentId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ ...student, Remark: student.Remark }),
        });
  
        if (!response.ok) {
          throw new Error("Failed to update remark.");
        }
  
        setSuccess(`Remark updated for student ID: ${studentId}`);
      } catch (error) {
        console.error(error.message);
        alert("Failed to save the remark. Please try again.");
      }
    };
  
  
  
  

  // Handle CA and Exam updates and dynamically calculate the Score
  const handleResultChange = (field, value) => {
    setUpdatedResult((prev) => {
      const updatedFields = { ...prev, [field]: Number(value) || 0 };

      // Update the score dynamically if CA or Exam changes
      if (field === "CA1" || field === "CA2" || field === "ClassWork" || field === "Project" || field === "Exam") {
        updatedFields.Score = (updatedFields.CA1 || 0) + (updatedFields.CA2 || 0) +  (updatedFields.ClassWork || 0) +  (updatedFields.Project || 0) +  (updatedFields.Exam || 0);
        updatedFields.Date = new Date().toLocaleString("en-US", {
            timeZone: "UTC",
            dateStyle: "full",
            timeStyle: "long",
          });
      }

      return updatedFields;
    });
  };

  // Update student's result
  const handleResultUpdate = async () => {
    if (!selectedStudent || !subjectToUpdate) return;

    try {
        setLoading(true);
        setError(null);

        // Find the student and update the specific result
        const updatedStudent = {
            ...selectedStudent,
            Results: selectedStudent.Results.map((result) =>
                result.Subject === subjectToUpdate
                    ? { ...result, ...updatedResult }
                    : result
            ),
        };

        // Send the updated student to the server
        const response = await fetch(`${config.apiEndpoint}/${selectedStudent.Class}/${selectedStudent.id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updatedStudent),
        });

        if (!response.ok) throw new Error("Failed to update result.");

        // Update local state with the modified student
        const updatedList = students.map((student) =>
            student.id === selectedStudent.id ? updatedStudent : student
        );

        setStudents(updatedList);
        setSelectedStudent(null); // Close modal
        setSubjectToUpdate("");
        setUpdatedResult({});
    } catch (err) {
        console.error(err.message);
        setError(`Error updating the result. ${err.message}`);
    } finally {
        setLoading(false);
    }
  };

  

  useEffect(() => {
    if (user) fetchStudents();
  }, [user]);


  return (
    <div className="bg-gray-100 min-h-screen p-6">
      {/* Header */}
      <header className="bg-yellow-400 p-6 rounded-lg shadow-md flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Welcome, {user?.Name}!</h1>
        <button
          onClick={() => {
            localStorage.removeItem("user");
            navigate("/");
          }}
          className="bg-yellow-700 text-white px-4 py-2 rounded-lg hover:bg-yellow-800"
        >
          Logout
        </button>
      </header>

      {/* Main Content */}
      <main className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-bold mb-4">Manage Students</h2>

        {loading ? (
          <p>Loading...</p>
        ) : error ? (
          <p className="text-red-500">{error}</p>
        ) : students.length === 0 ? (
          <p>No students found for your section and classes.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {students.map((student) => (
              <div
                key={student.id}
                className="p-4 bg-yellow-100 border rounded shadow"
              >
                <h3 className="font-bold text-lg mb-2">{student.Name}</h3>
                <p>Class: {student.Class}</p>
                <p>Section: {student.Section}</p>
                {/* Display Current Remark */}
                {user?.Status === "Principal" &&
                <div className="mt-4">
                  <h4 className="font-bold">Remark:</h4>
                  <input
                    type="text"
                    value={student.Remark || ''}
                    onChange={(e) => handleRemarkChange(student.id, e.target.value)}
                    className="w-full p-2 border rounded mt-2"
                  />
                  {success && <p className="text-green-500">{success}</p>}
                  <button
                    onClick={() => saveRemark(student.id)}
                    className="mt-2 bg-yellow-700 text-white px-4 py-2 rounded hover:bg-yellow-800"
                  >
                    Save Remark
                  </button>
                </div>
                }
                <h4 className="font-bold mt-4">Results:</h4>
                <ul>
                  {student.Results.map((result, index) =>
                    user.Subjects.includes(result.Subject) ? (
                      <li key={index} className="mb-2">
                        <strong>{result.Subject}</strong>: {result.Score} (
                        {result.Comment}){" "}
                        <button
                          onClick={() => {
                            setSelectedStudent(student);
                            setSubjectToUpdate(result.Subject);
                            setUpdatedResult(result);
                          }}
                          className="text-blue-500 underline"
                        >
                          Edit
                        </button>
                      </li>
                    ) : null
                  )}
                </ul>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Edit Modal */}
      {selectedStudent && subjectToUpdate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded shadow-lg max-w-sm w-full">
            <h2 className="font-bold text-lg mb-4">Edit {subjectToUpdate}</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleResultUpdate();
              }}
            >
              <label className="block mb-2">
                CA 1:
                <input
                  type="number"
                  value={updatedResult.CA1 || ""}
                  onChange={(e) => handleResultChange("CA1", e.target.value)}
                  className="w-full p-2 border rounded"
                />
              </label>
              <label className="block mb-2">
                CA 2:
                <input
                  type="number"
                  value={updatedResult.CA2 || ""}
                  onChange={(e) => handleResultChange("CA2", e.target.value)}
                  className="w-full p-2 border rounded"
                />
              </label>
              <label className="block mb-2">
                Classwork:
                <input
                  type="number"
                  value={updatedResult.ClassWork || ""}
                  onChange={(e) => handleResultChange("ClassWork", e.target.value)}
                  className="w-full p-2 border rounded"
                />
              </label>
              <label className="block mb-2">
                Project:
                <input
                  type="number"
                  value={updatedResult.Project || ""}
                  onChange={(e) => handleResultChange("Project", e.target.value)}
                  className="w-full p-2 border rounded"
                />
              </label>
              <label className="block mb-2">
                Exam:
                <input
                  type="number"
                  value={updatedResult.Exam || ""}
                  onChange={(e) => handleResultChange("Exam", e.target.value)}
                  className="w-full p-2 border rounded"
                />
              </label>
              <label className="block mb-2">
                Remark:
                <input
                  type="text"
                  value={updatedResult.Comment || ""}
                  onChange={(e) =>
                    setUpdatedResult((prev) => ({
                      ...prev,
                      Comment: e.target.value,
                    }))
                  }
                  className="w-full p-2 border rounded"
                />
              </label>
              <label className="block mb-2">
                Score:
                <input
                  type="number"
                  value={updatedResult.Score || ""}
                  readOnly
                  className="w-full p-2 border rounded bg-gray-200"
                />
              </label>
              <div className="flex justify-end mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedStudent(null);
                    setSubjectToUpdate("");
                  }}
                  className="mr-2 bg-gray-500 text-white px-4 py-2 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-yellow-700 text-white px-4 py-2 rounded"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherDash;
